import {
  faGear,
  faDollarCircle,
  faLock,
} from '@fortawesome/pro-light-svg-icons';
import UserPlaceholder from '../../assets/icons/user-placeholder.png?component';
import {
  faBadgePercent,
  faChartLineUp,
  faEnvelope,
  faEnvelopeOpenDollar,
  faFilm,
  faFire,
  faHome,
  faMoneyBillTransfer,
  faPhotoFilm,
  faRectangleAd,
  faSignalStream,
  faSquareList,
  faTag,
  faUser,
  faUsers,
  faUsersRectangle,
  faLaptopMobile,
} from '@fortawesome/pro-regular-svg-icons';

import { PATHS } from '../../../routes';
import { externalLink, Role } from './helpers';
import { NavMenu, NavMenuProps } from '@design-system/components/menu/NavMenu';
import {
  menuItemBadgeGreen,
  menuItemBadgeOrange,
  menuItemCaption,
} from '@design-system/components/menu/navMenu.css';
import { faPeopleGroup } from '@fortawesome/pro-solid-svg-icons';
import { externalUrls } from '@client/new/pages/creator/home/mappers';
import { StrategyGuideLink } from '../StrategyGuideLink';
import {
  getLivestreamingEnabled,
  getDmEnabled,
  getCommunityEnabled,
} from '@client/new/common/features.helpers';
import { isEligibleForOnboarding } from '@client/new/pages/onboarding/model/onboardingEligibility';

export const CreatorContextMenu = ({
  influencer,
  role,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  influencer: any;
  role: Role;
}) => {
  const pathParams: { communityId?: number; influencerId: number } = {
    communityId: influencer.communityId,
    influencerId: influencer.id,
  };

  const flags: MenuItemsParams['flags'] = {
    isLivestreamingEnabled: getLivestreamingEnabled(influencer?.features),
    isDmEnabled: getDmEnabled(influencer?.features),
    isCommunityEnabled: getCommunityEnabled(influencer?.features),
    isUnifiedProgramsEnabled:
      !!influencer?.customizations?.isUnifiedProgramsEnabled,
  };

  const menuList = getMenuItems({ flags, params: pathParams, role });
  const avatar = influencer.mediaCover
    ? `${influencer.mediaCover?.cdn01}${influencer.mediaCover?.image}`
    : UserPlaceholder;
  const isEligibleForGuide = isEligibleForOnboarding(
    influencer?.createdAt ?? null,
  );

  const displayName = `${influencer.firstName} ${influencer.lastName}`;

  return (
    <NavMenu
      list={menuList}
      onLogout={() => null}
      showProgress={false}
      isAdmin={true}
      displayName={displayName}
      avatar={avatar}
      topContent={
        isEligibleForGuide ? (
          <StrategyGuideLink influencerId={influencer.id} />
        ) : null
      }
    />
  );
};

const offersItem = (influencerId: number) => ({
  caption: (
    <span className={menuItemCaption} data-testid="menu-item-offers">
      Offers
      <span className={menuItemBadgeGreen}>NEW</span>
    </span>
  ),
  to: PATHS.BUNDLES(influencerId),
  icon: faTag,
  testId: 'Bundles',
});

const programsItem = (influencerId: number) => ({
  caption: 'Programs',
  to: PATHS.PROGRAMS(influencerId),
  icon: faFilm,
  testId: 'MainMenuItemPrograms',
});

const landingPageItem = (influencerId: number) => ({
  caption: 'Landing Page',
  to: PATHS.LANDING_PAGE(influencerId),
  icon: faLaptopMobile,
  testId: 'MainMenuItemLandingPage',
});

const commonMenuItems = ({ flags, params }: MenuItemsParams) => [
  {
    section: 'Home',
    items: [
      {
        caption: 'Home',
        to: PATHS.HOME_CREATOR(params.influencerId),
        icon: faHome,
        testId: 'MainMenuItemHome',
      },
    ],
  },
  ...(flags.isUnifiedProgramsEnabled
    ? [
        {
          section: 'SELL',
          items: [
            offersItem(params.influencerId),
            landingPageItem(params.influencerId),
          ],
        },
      ]
    : []),
  ...(flags.isUnifiedProgramsEnabled
    ? []
    : [
        {
          section: 'Products',
          items: [
            {
              caption: 'One Time Purchases',
              to: PATHS.PURCHASES(params.influencerId),
              icon: faDollarCircle,
              testId: 'MainMenuItemOTP',
            },
            {
              caption: 'Subscription',
              to: PATHS.SUBSCRIPTIONS(params.influencerId),
              icon: faMoneyBillTransfer,
              testId: 'MainMenuItemSubscription',
            },
            offersItem(params.influencerId),
          ],
        },
      ]),
  {
    section: 'Content',
    items: [
      {
        caption: 'Library',
        to: PATHS.LIBRARY(params.influencerId),
        icon: faPhotoFilm,
        testId: 'MainMenuItemLibrary',
      },
      {
        caption: 'Workouts',
        to: PATHS.PARTNER_WORKOUTS(params.influencerId),
        icon: faSquareList,
        subPaths: [
          PATHS.PARTNER_WORKOUTS_DRAFTS(params.influencerId),
          PATHS.PARTNER_WORKOUTS_SCHEDULED(params.influencerId),
          PATHS.PARTNER_WORKOUTS_PUBLISHED(params.influencerId),
          PATHS.PARTNER_WORKOUTS_ARCHIVED(params.influencerId),
        ],
        testId: 'MainMenuItemContent',
      },
      ...(flags.isUnifiedProgramsEnabled
        ? [programsItem(params.influencerId)]
        : []),
      {
        caption: 'Extras',
        to: PATHS.EXTRAS(params.influencerId),
        icon: faFire,
        testId: 'MainMenuItemExtras',
      },
    ],
  },
  {
    section: 'Education',
    items: [
      externalLink(
        'Creator Community',
        externalUrls['community'],
        faPeopleGroup,
      ),
    ],
  },
];

const roleBasedSocialItems = ({ flags, params, role }: MenuItemsParams) => {
  if (
    (flags.isLivestreamingEnabled ||
      flags.isDmEnabled ||
      flags.isCommunityEnabled) &&
    (role === 'admin' || role === 'moderator')
  ) {
    return [
      {
        section: 'Social',
        items: [
          ...(flags.isCommunityEnabled
            ? [
                {
                  caption: 'Community',
                  to: params.communityId
                    ? PATHS.COMMUNITY(params.influencerId, params.communityId)
                    : PATHS.COMMUNITY_INIT(params.influencerId),
                  icon: faUsers,
                  notificationCount: null,
                  testId: 'MainMenuItemCommunity',
                },
              ]
            : []),
          ...(flags.isDmEnabled
            ? [
                {
                  caption: 'Direct Messages',
                  to: PATHS.INBOX_MESSAGE(params.influencerId),
                  icon: faEnvelope,
                  testId: 'MainMenuItemDirectMessages',
                },
              ]
            : []),
          ...(flags.isLivestreamingEnabled
            ? [
                {
                  caption: 'Livestream',
                  to: PATHS.LIVESTREAM_VIEW(params.influencerId),
                  icon: faSignalStream,
                  testId: 'MainMenuItemLivestream',
                },
              ]
            : []),
        ],
      },
    ];
  }
  return [];
};

const roleBasedToolsItems = ({ params, role }: MenuItemsParams) => {
  if (role === 'admin' || role === 'moderator') {
    return [
      {
        section: 'Tools',
        items: [
          {
            caption: 'Audience',
            to: PATHS.CRM_CREATOR(params.influencerId, 'users'),
            icon: faUsersRectangle,
            testId: 'MainMenuItemAudience',
          },
          {
            caption: 'Analytics',
            to: PATHS.PARTNER_ANALYTICS(params.influencerId),
            icon: faChartLineUp,
            testId: 'MainMenuItemAnalytics',
          },
          {
            caption: 'Payouts',
            to: PATHS.ADMIN_PAYMENT(params.influencerId),
            icon: faEnvelopeOpenDollar,
            testId: 'MainMenuItemPayouts',
          },
          {
            caption: (
              <span className={menuItemCaption}>
                Marketing
                <span className={menuItemBadgeOrange}>beta</span>
              </span>
            ),
            to: PATHS.MARKETING(params.influencerId),
            icon: faRectangleAd,
            testId: 'MainMenuItemMarketing',
          },
          {
            caption: 'Promotions',
            to: PATHS.PROMOTIONS(params.influencerId),
            icon: faBadgePercent,
            testId: 'MainMenuItemPromotions',
          },
        ],
      },
    ];
  }
  return [];
};

const roleBasedAccountItems = ({ params, role }: MenuItemsParams) => {
  if (role === 'admin' || role === 'moderator') {
    return [
      {
        section: 'Account',
        items: [
          {
            caption: 'Profile',
            to: PATHS.PARTNER_EDIT(params.influencerId),
            icon: faUser,
            testId: 'MainMenuItemProfile',
          },
          {
            caption: 'Settings',
            to: PATHS.PARTNER_SETTINGS(params.influencerId),
            icon: faGear,
            testId: 'MainMenuItemSettings',
          },
          {
            caption: 'Administration',
            to: PATHS.ADMIN_ADMINISTRATION(params.influencerId),
            icon: faLock,
            testId: 'MainMenuItemAdministration',
          },
        ],
      },
    ];
  }
  if (role === 'contentEditor') {
    return [
      {
        section: 'Account',
        items: [
          {
            caption: 'Profile',
            to: PATHS.PARTNER_EDIT(params.influencerId),
            icon: faUser,
            testId: 'MainMenuItemProfile',
          },
        ],
      },
    ];
  }
  return [];
};

export type MenuItemsParams = {
  flags: Partial<{
    isLivestreamingEnabled: boolean;
    isDmEnabled: boolean;
    isCommunityEnabled: boolean;
    isUnifiedProgramsEnabled: boolean;
    canAdTracking: boolean;
  }>;
  params: { influencerId: number; communityId?: number };
  role: Role;
};

export const getMenuItems = (p: MenuItemsParams): NavMenuProps['list'] => [
  ...commonMenuItems(p),
  ...roleBasedSocialItems(p),
  ...roleBasedToolsItems(p),
  ...roleBasedAccountItems(p),
];
